import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, TrendingUp, Users, FileText, Calculator, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const FuerEigentuemer = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Maximaler Verkaufspreis',
      description: 'Durch strukturierte Vorbereitung und gezielte Käuferansprache erzielen wir optimale Konditionen für Ihre Handelsimmobilie.'
    },
    {
      icon: Users,
      title: 'Zugang zu allen Käufergruppen',
      description: 'Institutionelle Investoren, Family Offices und Privatinvestoren – wir kennen die Anforderungen jeder Käufergruppe.'
    },
    {
      icon: FileText,
      title: 'Professionelle Aufbereitung',
      description: 'Von der Unterlagen-Checkliste bis zum Exposé: Wir bereiten Ihre Immobilie optimal für den Markt vor.'
    }
  ];

  const processSteps = [
    { title: 'Erstgespräch & Analyse', description: 'Kostenlose Ersteinschätzung Ihrer Immobilie' },
    { title: 'Unterlagen & Vorbereitung', description: 'Zusammenstellung aller relevanten Dokumente' },
    { title: 'Bewertung & Strategie', description: 'Professionelle Marktanalyse und Preisstrategie' },
    { title: 'Käuferansprache', description: 'Gezielte Vermarktung an passende Investoren' },
    { title: 'Verhandlung', description: 'Professionelle Verhandlungsführung' },
    { title: 'Abwicklung', description: 'Begleitung bis zum Notartermin' }
  ];

  const faq = [
    {
      question: 'Was kostet die Vermittlung?',
      answer: 'Die Provision richtet sich nach dem Verkaufspreis und wird individuell vereinbart. In einem unverbindlichen Erstgespräch klären wir transparent alle Kosten und Leistungen.'
    },
    {
      question: 'Wie lange dauert der Verkauf?',
      answer: 'Die Verkaufsdauer hängt von Objektqualität, Mietvertragssituation und Marktlage ab. In der Regel sollten Sie mit 6-12 Monaten rechnen. Gut vorbereitete Objekte können schneller verkauft werden.'
    },
    {
      question: 'Welche Unterlagen benötige ich?',
      answer: 'Mietverträge inkl. Nachträge, Grundbuchauszug, Flächenpläne, Betriebskostenabrechnungen der letzten 3 Jahre, Energieausweis und ggf. Baugenehmigungen. Wir erstellen mit Ihnen eine vollständige Checkliste.'
    },
    {
      question: 'Muss ich die Mieter informieren?',
      answer: 'Ja, Mieter haben ein gesetzliches Vorkaufsrecht. Wir begleiten Sie bei der korrekten und zeitgerechten Information und stellen sicher, dass alle rechtlichen Anforderungen erfüllt werden.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Für Eigentümer – Handelsimmobilie erfolgreich verkaufen"
        description="Professionelle Begleitung beim Verkauf Ihrer Handelsimmobilie. Von der Vorbereitung bis zum Notartermin – mit strukturiertem Prozess und Zugang zu allen Käufergruppen."
        url="/fuer-eigentuemer"
      />
      <Navbar />
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-blue-50 to-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6"></div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
                Ihre Handelsimmobilie erfolgreich verkaufen
              </h1>
              <p className="text-lg leading-relaxed mb-8" style={{ color: 'var(--gray-700)' }}>
                Sie besitzen einen Supermarkt, Fachmarkt oder ein Fachmarktzentrum und möchten verkaufen? 
                Wir begleiten Sie professionell durch den gesamten Prozess von der Vorbereitung bis zum erfolgreichen Abschluss.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/kontakt">
                  <Button className="btn-gold-gradient text-white border-0 font-semibold px-8 py-6">
                    Kostenlose Ersteinschätzung
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/tools/wault">
                  <Button variant="outline" className="font-semibold px-8 py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                    <Calculator className="mr-2 h-5 w-5" />
                    WAULT kostenlos berechnen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Warum RetailInvest?
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-600)' }}>
                Spezialisierung auf Handelsimmobilien bedeutet tiefes Marktverständnis und direkte Kontakte zu Investoren
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="glass-card p-8 border-t-4" style={{ borderTopColor: 'var(--gold-600)' }}>
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                      {benefit.title}
                    </h3>
                    <p style={{ color: 'var(--gray-700)' }}>
                      {benefit.description}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Unsere Leistungen */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Was wir für Sie tun
              </h2>
              <p className="text-lg max-w-2xl mx-auto mb-12" style={{ color: 'var(--gray-600)' }}>
                Umfassende Begleitung vom ersten Gespräch bis zum erfolgreichen Verkauf
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="glass-card p-8">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>Vorbereitung & Analyse</h3>
                <ul className="space-y-3" style={{ color: 'var(--gray-700)' }}>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Kostenlose Ersteinschätzung des Objektwerts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>WAULT-Berechnung und Optimierungsvorschläge</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Unterlagen-Checkliste und Vollständigkeitsprüfung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Marktanalyse und Wettbewerbsvergleich</span>
                  </li>
                </ul>
              </Card>

              <Card className="glass-card p-8">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>Vermarktung & Verkauf</h3>
                <ul className="space-y-3" style={{ color: 'var(--gray-700)' }}>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Professionelles Exposé mit allen relevanten Kennzahlen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Gezielte Ansprache passender Käufergruppen bundesweit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Verhandlungsführung und Angebotsprüfung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span>Due Diligence-Begleitung bis zum Notartermin</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Provisionen */}
            <Card className="glass-card p-10 border-l-4" style={{ borderLeftColor: 'var(--gold-600)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2">
                  <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                    Transparente Konditionen
                  </h3>
                  <p className="text-lg mb-4" style={{ color: 'var(--gray-700)' }}>
                    Unsere Provision richtet sich nach dem Verkaufspreis und der Komplexität des Objekts:
                  </p>
                  <p className="text-xl font-bold mb-2" style={{ color: 'var(--gold-600)' }}>
                    1-3% zzgl. MwSt. vom Kaufpreis
                  </p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                    Die genaue Provision wird im Erstgespräch individuell vereinbart und hängt ab von: 
                    Objektgröße, Mietvertragssituation, WAULT und Vermarktungsaufwand.
                  </p>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Beispielrechnung:</p>
                  <p className="text-sm mb-1" style={{ color: 'var(--gray-700)' }}>Verkaufspreis: 2.000.000 €</p>
                  <p className="text-sm mb-1" style={{ color: 'var(--gray-700)' }}>Provision 2,5%: 50.000 €</p>
                  <p className="text-sm" style={{ color: 'var(--gray-700)' }}>zzgl. MwSt.: 59.500 €</p>
                  <p className="text-xs mt-3" style={{ color: 'var(--gray-500)' }}>Fällig bei Kaufpreiszahlung</p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Unser Verkaufsprozess
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-600)' }}>
                6 Schritte zum erfolgreichen Verkauf Ihrer Handelsimmobilie
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processSteps.map((step, index) => (
                <Card key={index} className="glass-card p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md" style={{ background: 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)' }}>
                      <span className="text-xl font-bold text-white">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-bold mb-2" style={{ color: 'var(--navy-800)' }}>
                        {step.title}
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Häufig gestellte Fragen
              </h2>
            </div>

            <div className="space-y-4">
              {faq.map((item, index) => (
                <Card key={index} className="glass-card p-6">
                  <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
                    {item.question}
                  </h3>
                  <p style={{ color: 'var(--gray-700)' }}>
                    {item.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit für den Verkauf?
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Vereinbaren Sie ein unverbindliches Erstgespräch und erfahren Sie, wie wir Ihre Handelsimmobilie erfolgreich verkaufen.
            </p>
            <Link to="/kontakt">
              <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FuerEigentuemer;